import { env } from "@/config/env";
import { getCurrentUserWithOrg, signInWithEmail, signInWithGoogle, signInWithOrg, signOut, signUpWithOrg, validateJWTClaims } from "@/lib/auth";
import { handleAuthError, supabase } from "@/lib/supabaseClient";
import { isPublicAuthConfigured } from "@/lib/authErrorHandler";
import { ExtendedUser } from "@/types/user";
import { Session } from "@supabase/supabase-js";
import React, { createContext, useContext, useEffect, useState } from "react";

interface AuthContextType {
  user: ExtendedUser | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  orgId: string | null;

  // Core auth methods
  signInWithEmail: (email: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;

  // Enhanced auth methods with org support
  signUpWithOrg: (options: any) => Promise<{ error: any }>;
  signInWithOrg: (options: any) => Promise<{ error: any }>;
  getCurrentUserWithOrg: () => Promise<any>;
  validateJWTClaims: () => Promise<any>;

  // Additional auth methods that components expect
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (
    email: string,
    password: string,
    metadata?: any,
  ) => Promise<{ error: any }>;
  signInWithProvider: (provider: string) => Promise<{ error: any }>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  resendConfirmation: (email: string) => Promise<{ error: any }>;
  updateProfile: (updates: any) => Promise<{ error: any }>;
  logout: () => Promise<void>;

  // Utility methods
  clearError: () => void;
}

// Export AuthUser type for other files
export type AuthUser = ExtendedUser;

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);

  // Initialize auth state
  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // Get initial session
        const { data } = await supabase.auth.getSession();

        if (mounted) {
          if (data.session) {
            setUser(data.session.user as ExtendedUser);
            setSession(data.session);
          }
        }
      } catch (err) {
        if (mounted) {
          console.error("Auth initialization error:", err);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (session?.user) {
        setUser(session.user as ExtendedUser);
        setSession(session);
        
        // Get user with org context
        const userWithOrg = await getCurrentUserWithOrg();
        if (userWithOrg?.org_id) {
          setOrgId(userWithOrg.org_id);
        }
      } else {
        setUser(null);
        setSession(null);
        setOrgId(null);
      }

      setLoading(false);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const clearError = () => setError(null);

  // Additional auth methods
  const signIn = async (email: string, password: string) => {
    setError(null);
    if (!email.trim() || !password) {
      const inputError = new Error("Email and password are required");
      setError("Enter your email and password.");
      return { error: inputError };
    }

    if (!isPublicAuthConfigured(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_ANON_KEY,
      import.meta.env.DEV,
    )) {
      const configError = new Error("Authentication service is not configured");
      setError(handleAuthError(configError));
      return { error: configError };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim().toLowerCase(),
        password,
      });
      if (error) setError(handleAuthError(error));
      return { error };
    } catch (cause) {
      const failure = cause instanceof Error ? cause : new Error("Sign-in request failed");
      setError(handleAuthError(failure));
      return { error: failure };
    }
  };

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      // Check if we should disable email confirmation
      const disableEmailConfirmation =
        env.VITE_DISABLE_EMAIL_CONFIRMATION === "true";

      // First, sign up the user
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata || {},
          emailRedirectTo: disableEmailConfirmation
            ? undefined
            : `${window.location.origin}/auth/callback`,
        },
      });

      if (error) {
        return { error };
      }

      // Auto-confirmation for development/testing ONLY - removed for security
      // This feature should only be enabled in controlled testing environments
      // with proper admin oversight, not in production signups
      if (
        env.VITE_AUTO_CONFIRM_USERS === "true" && data.user?.id &&
        import.meta.env.DEV
      ) {
        console.warn("⚠️ Auto-confirmation enabled - development mode only");
        // Note: Auto-confirmation requires admin privileges via /api/admin/secure-user-operations
        // Regular users cannot auto-confirm themselves - this would require an authenticated admin
      }

      // Supabase sends verification email automatically on signup
      // Users can resend via /auth/verify-email page if needed
      return { data, error: null };
    } catch (err) {
      console.error("Signup error:", err);
      return { error: err };
    }
  };

  const signInWithProvider = async (provider: string) => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: provider as any,
    });
    return { error };
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email);
    return { error };
  };

  const resendConfirmation = async (email: string) => {
    // Try custom Resend API endpoint first for better deliverability
    try {
      const token = session?.access_token;
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch("/api/auth/send-verification-email", {
        method: "POST",
        headers,
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        return { error: null };
      }

      // If custom API fails, fall back to Supabase
      const errorData = await response.json().catch(() => ({}));
      console.warn("Resend API failed, falling back to Supabase:", errorData);
    } catch (err) {
      console.warn("Resend API error, falling back to Supabase:", err);
    }

    // Fallback: Use Supabase's built-in resend functionality
    const { error } = await supabase.auth.resend({
      type: "signup",
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/callback` },
    });

    return { error };
  };

  const ALLOWED_PROFILE_FIELDS = [
    "full_name",
    "display_name",
    "avatar_url",
    "phone",
    "bio",
    "timezone",
    "language",
    "data",
  ] as const;

  const updateProfile = async (updates: any) => {
    try {
      // Update auth user data
      const { error: authError } = await supabase.auth.updateUser(updates);
      if (authError) {
        return { error: authError };
      }

      // Update profile in database if user exists
      if (user?.id) {
        // Whitelist allowed fields to prevent mass-assignment of role, id, etc.
        const safeUpdates: Record<string, unknown> = {};
        for (const key of ALLOWED_PROFILE_FIELDS) {
          if (key in updates) {
            safeUpdates[key] = updates[key];
          }
        }
        safeUpdates.updated_at = new Date().toISOString();

        const { error: profileError } = await supabase
          .from("profiles")
          .update(safeUpdates)
          .eq("id", user.id);

        if (profileError) {
          console.error("Profile update error:", profileError);
          return { error: profileError };
        }
      }

      return { error: null };
    } catch (err) {
      console.error("Update profile error:", err);
      return { error: err };
    }
  };

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.assign("/auth/login");
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    error,
    orgId,
    signInWithEmail,
    signInWithGoogle,
    signOut,
    signUpWithOrg,
    signInWithOrg,
    getCurrentUserWithOrg,
    validateJWTClaims,
    signIn,
    signUp,
    signInWithProvider,
    resetPassword,
    resendConfirmation,
    updateProfile,
    logout,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
